/**
 * ToastContext — feedback global no intrusivo para acciones.
 *
 * Cualquier acción operacional (asignar, despachar, exportar, descartar)
 * confirma con un toast en la esquina inferior derecha que se
 * autodescarta.  Mantiene la UI "viva": ninguna interacción queda sin
 * respuesta visible.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type ToastTone = "info" | "success" | "warn" | "danger";

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastContextValue {
  toast: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((message: string, tone: ToastTone = "info") => {
    const id = ++counter;
    setItems((prev) => [...prev, { id, message, tone }]);
    window.setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, 3600);
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);

  const toneColor: Record<ToastTone, string> = {
    info: "var(--c-info)",
    success: "var(--c-success)",
    warn: "var(--c-warn)",
    danger: "var(--c-danger)",
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        style={{
          position: "fixed",
          bottom: 48,
          right: 24,
          zIndex: 240,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          pointerEvents: "none",
        }}
      >
        {items.map((t) => (
          <div
            key={t.id}
            className="toast fade-in"
            style={{ borderLeft: `3px solid ${toneColor[t.tone]}`, position: "static" }}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error("useToast must be used within <ToastProvider>");
  }
  return ctx;
}
